import { AnalysisWidget } from '@go-widgets/well-logs-widget';
import { FullArrowLight as FullIcon, ReduceLight as MinimizeIcon } from '@profgeosoft-ui/icons';
import { Button, Tooltip } from '@profgeosoft-ui/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useService } from 'src/packages/di';

import type { WellAnalyseWidgetEntity } from './WellAnalyseWidget.entity';
import type { TWidgetRendererOptions } from '../workspace/services/WidgetRendererService';
import type { WidgetStateParams } from '@go-widgets/well-logs-widget/dist/AnalysisWidget/types';
import type { TCreateWidgetOptions } from 'src/entities/tab/TabEntity';

import { NoWellChosen } from '../no-well-chosen';
import { WidgetHeader } from '../widget-header/WidgetHeader';

import { WellAnalyseWidgetStore } from './WellAnalyseWidget.store';

import styles from './WellAnalyseWidget.module.scss';

type WrapperProps = {
  entity: WellAnalyseWidgetEntity;
  options: TWidgetRendererOptions;
  onCreateWellAnalyseWidget(
    wellId: number | null,
    stateParams: Partial<WidgetStateParams> | null,
    options?: TCreateWidgetOptions
  ): void;
};

type Props = {
  store: WellAnalyseWidgetStore;
  entity: WellAnalyseWidgetEntity;
  options: TWidgetRendererOptions;
};

export const WellAnalyseWidgetWrapper = observer(function WellAnalyseWidgetWrapper({
  entity,
  options,
  onCreateWellAnalyseWidget,
}: WrapperProps) {
  const [store, setStore] = useState<WellAnalyseWidgetStore | null>(null);

  const storeService = useService('widgetStoreService');

  const navigate = useNavigate();

  useEffect(() => {
    if (store) return;

    function redirectToBasicParameters(wellId: number | null, wellboreId?: number | null) {
      const queryParams = new URLSearchParams();

      if (wellId) {
        queryParams.set('wellId', wellId.toString());
      }

      if (wellboreId) {
        queryParams.set('wellboreId', wellboreId.toString());
      }

      const path = `/settings?section=basicParameters&${queryParams.toString()}`;

      navigate(path);
    }

    const _store = storeService.getStore<WellAnalyseWidgetEntity, WellAnalyseWidgetStore>(entity);

    if (_store) {
      setStore(_store);
    } else {
      const newStore = new WellAnalyseWidgetStore(
        entity,
        options.groupSelectStore,
        onCreateWellAnalyseWidget,
        redirectToBasicParameters,
        options.wellListEntity
      );

      storeService.setStore<WellAnalyseWidgetEntity, WellAnalyseWidgetStore>(entity, newStore);

      setStore(newStore);
    }
  }, [
    setStore,
    storeService,
    entity,
    onCreateWellAnalyseWidget,
    options.groupSelectStore,
    store,
    navigate,
    options.wellListEntity,
  ]);

  if (!store) return null;

  return <WellAnalyseWidget store={store} entity={entity} options={options} />;
});

export const WellAnalyseWidget = observer(function WellAnalyseWidget({ entity, options, store }: Props) {
  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);
  const { setGroupForWellLogs, exitFullScreen, isReady, effect } = store;

  const { t } = useTranslation();

  const { wellId, isFullscreen, state, setFullscreen, wellboreName } = entity;

  useEffect(effect, [effect]);

  const directories = useService('directories');
  const wells = directories.getObject('GOdashboard_Well');
  const wellName = wells?.find((item) => item.id === wellId)?.data.name ?? wellId;

  return (
    <div className={styles.wellAnalyseWidgetWrapper} ref={(ref) => setWrapperElement(ref)}>
      <WidgetHeader
        entity={entity}
        onWidgetChangeGroup={setGroupForWellLogs}
        groupSelectStore={options.groupSelectStore}
        widgetName={entity.title}
        wellName={wellName}
        wellboreName={wellboreName}
        onWidgetDelete={() => {
          options.onWidgetDelete(entity);
        }}
        showCloseButton={!isFullscreen}
        draggable={!isFullscreen}
        renderAdditionalButtons={() => {
          if (!isReady) return null;

          return (
            <>
              {!isFullscreen && (
                <Tooltip title={t('common:Widget.fullscreen')} delay={400}>
                  <Button
                    onClick={() => setFullscreen(true)}
                    variant="flat"
                    icon={<FullIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              {isFullscreen && (
                <Tooltip title={t('common:Widget.collapse')} delay={400}>
                  <Button
                    onClick={() => exitFullScreen()}
                    variant="flat"
                    icon={<MinimizeIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
            </>
          );
        }}
      />
      {wrapperElement && (
        <div className={styles.wrapper}>
          {state ? <AnalysisWidget widgetState={state} externalContainer={wrapperElement} /> : <NoWellChosen />}
        </div>
      )}
    </div>
  );
});
